import { useParams, Navigate, Link } from "react-router-dom";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import SectionHeader from "@/components/SectionHeader";
import CTASection from "@/components/CTASection";
import { LinkButton } from "@/components/Button";
import CrmDashboard from "@/components/mockups/CrmDashboard";
import EngageDashboard from "@/components/mockups/EngageDashboard";
import { PRODUCTS } from "@/lib/content";

const ProductDetail = () => {
  const { slug } = useParams();
  const product = PRODUCTS.find((p) => p.slug === slug);
  if (!product) return <Navigate to="/404" replace />;
  const Mockup = product.mockup === "crm" ? CrmDashboard : EngageDashboard;

  return (
    <>
      <Seo title={`${product.name} | Kryvo Technologies`} description={product.description} />

      {/* Hero */}
      <section className="grid-bg bg-kryvo-mist pb-16 pt-[calc(72px+4rem)] sm:pb-20" data-testid="product-detail-hero">
        <div className="container-x grid items-center gap-12 lg:grid-cols-2">
          <Reveal>
            <Link to="/products" className="text-sm font-medium text-kryvo-green hover:text-kryvo-deep" data-testid="product-back-link">
              ← All Products
            </Link>
            <div className="mt-6">
              <span className="inline-block rounded-full bg-kryvo-lime/25 px-3.5 py-1.5 text-xs font-semibold text-kryvo-deep" data-testid="product-detail-badge">
                {product.badge}
              </span>
            </div>
            <h1 className="mt-4 font-display text-4xl font-semibold text-kryvo-ink sm:text-5xl">
              {product.name}
            </h1>
            <p className="mt-3 text-xl font-medium text-kryvo-green">{product.headline}</p>
            <p className="mt-5 max-w-lg text-base leading-relaxed text-kryvo-ink/65 sm:text-lg">
              {product.description}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <LinkButton to="/contact" variant="primary" testId="product-detail-cta">
                Let's Talk
              </LinkButton>
              <LinkButton to="/products" variant="outline" arrow={false} testId="product-detail-all">
                View All Products
              </LinkButton>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="relative">
              <div className="absolute -inset-3 sm:-inset-6 rounded-[2rem] bg-gradient-to-br from-kryvo-lime/25 via-white to-kryvo-green/10" aria-hidden="true" />
              <div className="relative aspect-[4/3]">
                <Mockup />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Overview */}
      <section className="container-x py-16 sm:py-20" data-testid="product-overview">
        <Reveal className="mx-auto max-w-3xl text-center">
          <span className="eyebrow justify-center">
            <span className="h-px w-6 bg-kryvo-lime" aria-hidden="true" />
            Product Overview
          </span>
          <h2 className="mt-4 text-balance text-3xl font-semibold text-kryvo-ink sm:text-4xl">
            Built for the way modern businesses work
          </h2>
          <p className="mt-5 text-base leading-relaxed text-kryvo-ink/65 sm:text-lg">{product.overview}</p>
        </Reveal>
      </section>

      {/* Features */}
      <section className="bg-kryvo-mist py-16 sm:py-20" data-testid="product-features">
        <div className="container-x">
          <SectionHeader eyebrow="Key Features" title={`What's inside ${product.name}`} />
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {product.features.map((f, i) => (
              <Reveal key={f.title} delay={i * 0.06}>
                <div className="h-full rounded-2xl border border-kryvo-green/10 bg-white p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_45px_-22px_rgba(6,78,42,0.3)]">
                  <span className="mb-4 block h-1.5 w-8 rounded-full bg-kryvo-lime" aria-hidden="true" />
                  <h3 className="text-lg font-semibold text-kryvo-ink">{f.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-kryvo-ink/60">{f.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* How it helps */}
      <section className="container-x py-16 sm:py-20" data-testid="product-how-it-helps">
        <div className="grid gap-10 lg:grid-cols-2 lg:items-center">
          <SectionHeader
            align="left"
            eyebrow="How It Helps"
            title="Real outcomes for your business"
            description={`${product.name} is designed around the everyday realities of running and growing a business.`}
          />
          <div className="grid gap-4 sm:grid-cols-2">
            {product.howItHelps.map((h, i) => (
              <Reveal key={h.title} delay={i * 0.06}>
                <div className="h-full rounded-2xl bg-kryvo-mist p-6">
                  <h3 className="font-semibold text-kryvo-deep">{h.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-kryvo-ink/60">{h.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Product UI spotlight */}
      <section className="border-y border-kryvo-line bg-kryvo-mist py-16 sm:py-24" data-testid="product-ui-spotlight">
        <div className="container-x">
          <div className="mx-auto max-w-2xl text-center">
            <span className="eyebrow justify-center">
              <span className="h-px w-6 bg-kryvo-lime" aria-hidden="true" />
              Inside the Product
            </span>
            <h2 className="mt-4 text-3xl font-semibold text-kryvo-ink sm:text-4xl">
              A workspace your team will love
            </h2>
          </div>
          <Reveal delay={0.15} className="mx-auto mt-12 max-w-4xl">
            <div className="overflow-hidden rounded-2xl border border-kryvo-line shadow-[0_40px_100px_-50px_rgba(8,122,61,0.35)]">
              <div className="flex items-center gap-1.5 border-b border-kryvo-green/10 bg-white px-4 py-2.5">
                <span className="h-2.5 w-2.5 rounded-full bg-kryvo-green/20" />
                <span className="h-2.5 w-2.5 rounded-full bg-kryvo-green/30" />
                <span className="h-2.5 w-2.5 rounded-full bg-kryvo-lime" />
              </div>
              <div className="aspect-[16/10] bg-white p-4 sm:p-6">
                <Mockup />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Use cases */}
      <section className="container-x py-16 sm:py-20" data-testid="product-use-cases">
        <SectionHeader eyebrow="Use Cases" title={`Who ${product.name} is for`} />
        <div className="mt-14 grid gap-6 sm:grid-cols-2">
          {product.useCases.map((u, i) => (
            <Reveal key={u.title} delay={i * 0.06}>
              <div className="h-full rounded-2xl border border-kryvo-green/10 bg-white p-7 transition-colors duration-300 hover:border-kryvo-green/30">
                <span className="font-display text-sm font-semibold text-kryvo-lime">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <h3 className="mt-2 text-xl font-semibold text-kryvo-ink">{u.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-kryvo-ink/60">{u.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <CTASection
        title={`Ready to see ${product.name} in action?`}
        description="Tell us about your business and we'll show you how it fits."
      />
    </>
  );
};

export default ProductDetail;
